/**
 * Upgrade links to special nodes.
 *
 * The purpose of this extension is to upgrade some links to our custom nodes.
 *
 * The document is first parsed to a ProseMirror document by MarkdownIt, and then
 * those links that satisfy the special link format are converted from link nodes
 * to custom node types.
 *
 */

import { Node } from "prosemirror-model";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { LinkedEntity } from ".";

export type LinkedEntityInfoPopupResult =
    | { popup: true; create: () => Element }
    | { popup: false };

export interface LinkedEntitiesManager<E extends LinkedEntity = LinkedEntity> {
    baseUrl: string;
    // The list of entities.
    entities: E[];
    // Event bus which is used to publish events, e.g. "entities:changed".
    events: EventTarget;
    nodeType: string;
    // Used to await until the manager is ready.
    ready: Promise<unknown>;
    // Sync represenation of the ready promise.
    isReady: boolean;

    check(href: string, text: string): RegExpMatchArray | false;
    extract(href: string, text: string): E;
    serialize(id: string, node: Node): string;
    getHref(entity: Omit<E, "href">): string;
    findById(id: string): E | void;
    findByName(name: string): E | void;
}

export class BaseLinkedEntitiesManager<E extends LinkedEntity = LinkedEntity>
    implements LinkedEntitiesManager<E>
{
    baseUrl: string;
    entities: E[] = [];
    events = new EventTarget();
    nodeType: string;
    ready = new Promise<unknown>((resolve) => resolve(null));
    isReady = false;

    organizationManager = useOrganizationManager();
    stekManager = useStekManager();

    constructor(baseUrl: string) {
        // Remove trailing slashes
        this.baseUrl = baseUrl.replace(/\/+$/, "");
        this.events = new EventTarget();

        // Regardless of how a child implementation sets the ready
        // promise, we update `isReady` when it resolves.
        this.ready.then(() => {
            this.isReady = true;
        });
    }

    /**
     * Regular expression which matches the URL.
     */
    get _hrefFormat() {
        return new RegExp(
            `^(${encodeURI(this.baseUrl)})?/` +
                "(?<orgName>[^/?&]+)/" +
                "steks/" +
                "(?<stekName>[^/?&]+)/" +
                "(?<type>[^/?&]+)/" +
                "(?<id>[A-Za-z0-9_-]+)"
        );
    }

    getHref(entity: Omit<E, "href">) {
        return encodeURI(
            (
                this.baseUrl +
                "/" +
                entity.orgName +
                "/steks/" +
                entity.stekName +
                "/" +
                this.nodeType +
                "/" +
                entity.id
            ).replace(/([^:])\/{2,}/g, "$1/")
        );
    }

    itemToEntity(item: Record<string | number | symbol, unknown>): E {
        const orgName = this.organizationManager.organization.value?.name;
        const stekName = this.stekManager.stek.value?.name;

        const entity = {
            name: item.name,
            id: item.identity,
            stekName: item.stekName || stekName,
            orgName: item.orgName || orgName,
            type: this.nodeType,
            data: item,
            state: "ready",
        } as E;
        entity.href = this.getHref(entity);
        return entity;
    }

    setEntities(items: Record<string | number | symbol, unknown>[]) {
        this.entities = items?.map(this.itemToEntity.bind(this)) ?? [];
        this.events.dispatchEvent(
            new CustomEvent("entities:changed", {
                detail: this.entities,
            })
        );
    }

    /**
     * Check whether the given href is matches this manager.
     */
    check(href: string, _text: string): RegExpMatchArray | false {
        const match = this._hrefFormat.exec(href);
        if (match && match.groups?.["type"] === this.nodeType) {
            return match;
        } else {
            return false;
        }
    }

    /**
     * Extract an entity from the given link and text.
     */
    extract(href: string, text: string): E {
        const orgName = this.organizationManager.organization.value?.name;
        const stekName = this.stekManager.stek.value?.name;

        if (!orgName || !stekName) {
            throw Error(
                "Organization and Stek need to be available before we can extract entities."
            );
        }

        const match = this.check(href, text);
        if (!match) {
            throw Error("No match found.");
        }

        const decodedStekMatch = decodeURIComponent(
            match.groups?.stekName || ""
        );
        const decodedOrgMatch = decodeURIComponent(match.groups?.orgName || "");

        if (decodedStekMatch !== stekName || decodedOrgMatch !== orgName) {
            return {
                type: this.nodeType,
                state: "external",
                stekName: match.groups?.stekName,
                orgName: match.groups?.orgName,
                href: href,
                name: text,
                id: match.groups?.["id"],
            } as E;
        } else {
            return (
                this.findById(match.groups?.["id"] || "") ||
                ({
                    type: this.nodeType,
                    state: "pending",
                    stekName: decodedStekMatch || stekName,
                    orgName: decodedOrgMatch || orgName,
                    href: href,
                    name: text,
                    id: match.groups?.["id"],
                } as E)
            );
        }
    }

    /**
     * Create the raw text for the given entity id.
     */
    serialize(entityId: string, node: Node) {
        const entity = this.findById(entityId);
        if (entity) {
            return `[${entity.name}](${this.getHref(entity)})`;
        } else {
            return `[${node.attrs.name}](${node.attrs.href})`;
        }
    }

    findById(id: string) {
        return this.entities?.find((entity) => entity.id === id);
    }

    findByName(name: string) {
        return this.entities?.find((entity) => entity.name === name);
    }
}
